'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchPlaceOfDeliveries } from '@eliadmin/core/src/api/places-of-delivery/places-of-delivery-api';
import type { PlaceOfDelivery } from '@eliadmin/core/src/api/reservation-medicine/reservation-medicine-api';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { GoArrowUpRight } from 'react-icons/go';
import { LuSearch } from 'react-icons/lu';
import { normalizeText } from '@eliadmin/core/src/utils/normalize-search-text';

export function FindUsContent({
  title,
  searchPlaceholder,
  noResultsLabel,
}: {
  title: string;
  searchPlaceholder: string;
  noResultsLabel: string;
}) {
  const locale = useLocaleStore(state => state.currentLocale);
  const { 'google-api': googleApiKey } = useGlobalStore(state => state.apiKeys);

  const { data: places } = useQuery({
    queryKey: [CacheKey.PLACE_OF_DELIVERY, locale],
    queryFn: () =>
      fetchPlaceOfDeliveries({ locale, query: { results: 9999 } })
        .then(res => res.data)
        .catch(() => [] as PlaceOfDelivery[]),
  });

  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [activePlaceId, setActivePlaceId] = useState<number | null>(null);

  const searchablePlaces = useMemo(
    () =>
      (places ?? []).map(place => ({
        place,
        text: normalizeText(
          place.name + place.address + place.street_number + place.city + place.postcode,
        ),
      })),
    [places],
  );
  const filteredPlaces = useMemo(() => {
    const search = normalizeText(deferredQuery);
    return searchablePlaces.filter(item => item.text.includes(search)).map(item => item.place);
  }, [searchablePlaces, deferredQuery]);

  // Keep exactly one place active: default to the first match, and drop the
  // selection if it filters out of view so we never point the map at a hidden place.
  useEffect(() => {
    if (filteredPlaces.length === 0) {
      setActivePlaceId(null);
      return;
    }
    if (!filteredPlaces.some(place => place.id === activePlaceId)) {
      setActivePlaceId(filteredPlaces[0].id);
    }
  }, [filteredPlaces, activePlaceId]);

  const activePlace = filteredPlaces.find(place => place.id === activePlaceId) ?? null;

  const mapSrc = activePlace
    ? googleApiKey?.token
      ? `https://www.google.com/maps/embed/v1/place?key=${googleApiKey.token}&q=${activePlace.latitude},${activePlace.longitude}`
      : `https://www.google.com/maps?q=${activePlace.latitude},${activePlace.longitude}&output=embed`
    : undefined;

  return (
    <>
      <div className="flex w-full flex-col items-start justify-start gap-6 md:max-w-[462px]">
        <Heading title={title} tag="h3" />
        <Input
          before={<LuSearch />}
          aria-label={searchPlaceholder}
          placeholder={searchPlaceholder}
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <div className="flex max-h-[400px] w-full flex-col items-start justify-start gap-2 overflow-y-scroll">
          {filteredPlaces.length > 0 ? (
            filteredPlaces.map(place => (
              <PlaceCard
                key={place.id}
                place={place}
                isActive={place.id === activePlaceId}
                onClick={() => setActivePlaceId(place.id)}
              />
            ))
          ) : (
            <Paragraph size="sm" color="foreground">
              {noResultsLabel}
            </Paragraph>
          )}
        </div>
      </div>
      {mapSrc && (
        <iframe
          key={activePlace?.id}
          title={activePlace?.name}
          className="h-[326px] w-full border-0 sm:h-[380px] md:h-[400px] md:max-w-[666px] lg:h-[564px]"
          src={mapSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
      )}
    </>
  );
}

function PlaceCard({
  place,
  isActive,
  onClick,
}: {
  place: PlaceOfDelivery;
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      className={cn(
        'group flex w-full cursor-pointer flex-col items-start justify-start gap-2 rounded-[16px] p-3 text-left sm:p-4',
        {
          'bg-[#E4F2E7]': isActive,
        },
      )}
      onClick={onClick}
    >
      <span className="flex w-full items-start justify-between">
        <span className="h6 font-semibold">{place.name}</span>
        <GoArrowUpRight
          size={20}
          className={cn('transition-all group-hover:text-primary', {
            'text-primary': isActive,
            'text-border': !isActive,
          })}
        />
      </span>
      <Paragraph tag="span" size="sm" color="foreground">
        {place.address} {place.street_number}, {place.city}
        <br />
        {place.postcode}, {place.country?.name}
      </Paragraph>
    </button>
  );
}
